import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { type UpdateClaimCampaignBody, updateClaimCampaign } from '~/services/LuxLoyalty';

import { claimsQueryKeys } from '../loyalty/claimsQueryKeys';

import { claimCampaignQueryKeys } from './claimCampaignQueryKeys';

interface UpdateClaimCampaignParams {
  campaignId: string;
  body: UpdateClaimCampaignBody;
}

/**
 * Updates an existing claim campaign and invalidates related caches.
 */
export function useUpdateClaimCampaign() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ campaignId, body }: UpdateClaimCampaignParams) => {
      return await updateClaimCampaign(campaignId, body);
    },
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: claimCampaignQueryKeys.all });
      // The enrolment list sits under a different key root. Changing matchCheckUntil changes which members are
      // Expired, so refresh it too
      queryClient.invalidateQueries({ queryKey: claimsQueryKeys.campaignEnrollmentsBase(campaignId) });
      enqueueSnackbar('Claim campaign updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating claim campaign: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
